import React, { useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Card,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Pagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Search from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { getAllVillagesAction, getAllFarmerCommitmentsAction, getFarmerCommitmentByIdAction, updateFarmerCommitmentAction } from '../../../store/actions';

// Helper to find reference by id in farmerCommitmentsData
function findReferenceById(farmerCommitmentsData, refId) {
  if (!farmerCommitmentsData?.data) return null;
  for (const commitment of farmerCommitmentsData.data) {
    const farmer = commitment.farmer;
    if (farmer && Array.isArray(farmer.references)) {
      for (const ref of farmer.references) {
        if (ref.id === refId) {
          return {
            ...ref,
            commitment_id: commitment.commitment_id,
            farmer_id: farmer.farmer_id,
            farmer_name: farmer.name,
          };
        }
      }
    }
  }
  return null;
}



function FarmerReferenceLead() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const [query, setQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const [page, setPage] = React.useState(1);
  const PAGE_SIZE = 30;
  const dispatch = useDispatch();
  // Dialog state
  const [editOpen, setEditOpen] = React.useState(false);
  const [editRefId, setEditRefId] = React.useState(null);
  const [editValues, setEditValues] = React.useState({ 
    name: '', mobile: '', village_id: '', status: '' 
  });
  const [editFarmerCommitmentId, setEditFarmerCommitmentId] = React.useState(null);

  // Fetch all villages and all farmer commitments on mount
  useEffect(() => {
    dispatch(getAllVillagesAction());
    dispatch(getAllFarmerCommitmentsAction());
  }, [dispatch]);

  // Get villages and farmer commitments from Redux
  const { successData: villagesData } = useSelector((state) => state.getAllVillagesReducer) || {};
  const { successData: farmerCommitmentsData } = useSelector((state) => state.getAllFarmerCommitmentsReducer) || {};
    // Listen for updateFarmerCommitmentAction success and refresh list
  const { successData: updateSuccess, error: updateError } = useSelector(state => state.updateFarmerCommitmentReducer || {});

  useEffect(() => {
    if (updateSuccess) {
      dispatch(getAllFarmerCommitmentsAction());
    }
  }, [updateSuccess, dispatch]);
  // Build village map (id -> name) for new API shape
  const villageMap = React.useMemo(() => {
    const villages = villagesData?.data?.villages;
    if (!Array.isArray(villages)) return {};
    const map = {};
    villages.forEach(v => {
      if (v?.villageId && v?.name) {
        map[v.villageId] = v.name;
      }
    });
    return map;
  }, [villagesData]);
  // For select options
  const villageList = React.useMemo(() => {
    const villages = villagesData?.data?.villages;
    if (!Array.isArray(villages)) return [];
    return villages.map(v => ({ id: v.villageId, name: v.name }));
  }, [villagesData]);

  // Extract all references from all farmer commitments
  const references = React.useMemo(() => {
    if (!farmerCommitmentsData?.data) return [];
    const refs = [];
    farmerCommitmentsData.data.forEach(commitment => {
      const farmer = commitment.farmer;
      if (farmer && Array.isArray(farmer.references)) {
        farmer.references.forEach(ref => {
          refs.push({
            ...ref,
            farmer_name: farmer.name,
            farmer_id: farmer.farmer_id,
          });
        });
      }
    });
    return refs;
  }, [farmerCommitmentsData]);

  // Status chip color mapping
  const getStatusChipStyles = (status) => {
    switch (status) {
      case "Converted":
        return { backgroundColor: "#D1FAE5", color: "#047857" };
      case "ReContact":
        return { backgroundColor: "#FFEFD6", color: "#B45309" };
      case "Pending":
        return { backgroundColor: "#FFEFD6", color: "#B45309" };
      case "Inactive":
        return { backgroundColor: "#FEE2E2", color: "#B91C1C" };
      default:
        return { backgroundColor: "#F3F4F6", color: "#6B7280" };
    }
  };

  // Filtering logic
  const filteredRows = React.useMemo(() => {
    let filtered = references;
    if (statusFilter && statusFilter.trim() !== "") {
      const statusNorm = statusFilter.trim().toLowerCase();
      filtered = filtered.filter(r => {
        const refStatus = (r.status || "").trim();
        // Only include if status matches and is not null/empty
        return refStatus && refStatus.toLowerCase() === statusNorm;
      });
    }
    const q = query.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((r) =>
        [r.name, r.mobile, r.id, r.village_id, r.farmer_name, r.farmer_id]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return filtered;
  }, [query, references, statusFilter]);

  // Reset to page 1 when filters change
  React.useEffect(() => { setPage(1); }, [query, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Handle edit click
  const handleEditClick = (refId) => {
    const ref = findReferenceById(farmerCommitmentsData, refId);
    if (ref) {
      setEditRefId(refId);
      setEditFarmerCommitmentId(ref.commitment_id);
      setEditValues({
        name: ref.name || '',
        mobile: ref.mobile || '',
        village_id: ref.village_id || '',
        status: ref.status || '',
      });
      setEditOpen(true);
      setStatusFilter(""); // Reset filter to 'All Status' when editing
      dispatch(getFarmerCommitmentByIdAction({ endPoint: `farmer-commitment/${refId}` }));
    }
  };

  // Handle dialog close
  const handleEditClose = () => {
    setEditOpen(false);
    setEditRefId(null);
    setEditValues({ name: '', mobile: '', village_id: '' });
    setEditFarmerCommitmentId(null);
  };

  // Handle dialog save
  const handleEditSave = () => {
    const commitment = (farmerCommitmentsData?.data || []).find(c => c.commitment_id === editFarmerCommitmentId);
    if (!commitment) return;
    // Build updated references array with edited values
    const updatedRefer = (commitment.farmer?.references || []).map(ref =>
      ref.id === editRefId
        ? {
            id: ref.id,
            name: editValues.name,
            mobile: editValues.mobile,
            district_id: ref.district_id,
            mandal_id: ref.mandal_id,
            village_id: editValues.village_id,
            status: editValues.status,
            acres: ref.acres,
          }
        : {
            id: ref.id,
            name: ref.name,
            mobile: ref.mobile,
            district_id: ref.district_id,
            mandal_id: ref.mandal_id,
            village_id: ref.village_id,
            status: ref.status,
            acres: ref.acres,
          }
    );
    dispatch(updateFarmerCommitmentAction({
      endPoint: `farmer-commitment/${editFarmerCommitmentId}`,
      farmer_id: commitment.farmer_id,
      season_id: commitment.season_id,
      crop_id: commitment.crop_id,
      variety_id: commitment.variety_id,
      expected_qty: commitment.expected_qty,
      expected_acre: commitment.expected_acre,
      expected_dealer_id: commitment.expected_dealer_id,
      refer: updatedRefer,
    }));
    dispatch(getAllFarmerCommitmentsAction()); // Immediate refresh
    handleEditClose();
  };

  const gridColsDesktop = "2.6fr 1.4fr 1.2fr 1.0fr 0.9fr";

  return (
    <Box sx={{ minHeight: '100vh', p: { xs: 0, md: 1 }, mt: { xs: 8, sm: 0 } }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 0 }}>
        Farmer Reference Lead
      </Typography>

      <Card sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              placeholder="Search ..."
              slotProps={{
                input: {
                  startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
                },
              }}
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              fullWidth
              sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="Converted">Converted</MenuItem>
              <MenuItem value="ReContact">ReContact</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Box sx={{ mt: 1 }}>
        {isMobile ? (
          <Grid container spacing={1} sx={{ px: 0.5 }}>
            {pagedRows.map((row) => (
              <Grid item xs={12} key={row.id} sx={{ px: 0, width: '100%' }}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    p: 1,
                    border: "1px solid #E5E7EB",
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    mb: 0.5,
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover, &:active": {
                      borderColor: "#2563EB",
                      boxShadow: "0 4px 14px rgba(37,99,235,0.18)",
                    },
                  }}
                >
                  <Box sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }}>
                    <Box mb={0.5} display="flex" alignItems="center" sx={{ width: '100%' }}>
                      <Typography fontWeight={600} minWidth={90} fontSize={14}>Farmer Identity:</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Typography variant="body2" fontSize={14}>{row.name}</Typography>
                        <Chip
                          label={row.status || "-"}
                          size="small"
                          sx={{ width: 90, height: 22, backgroundColor: row.status === "Converted" ? '#10B981' : '#F87171', color: '#fff', ml: 'auto', fontSize: 12 }}
                        />
                      </Box>
                    </Box>
                    <Box mb={0.5} display="flex" alignItems="center" gap={0} sx={{ width: '100%' }}>
                      <Typography fontWeight={600} minWidth={90} fontSize={14}>Mobile Number:</Typography>
                      <Typography variant="body2" fontSize={14}>{row.mobile}</Typography>
                    </Box>
                    <Box mb={0.5} display="flex" alignItems="center" gap={0} sx={{ width: '100%' }}>
                      <Typography fontWeight={600} minWidth={90} fontSize={14}>Village Name:</Typography>
                      <Typography variant="body2" fontSize={14}>{villageMap[row.village_id] || row.village_id || "-"}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0} justifyContent="flex-end" sx={{ width: '100%' }}>
                      <IconButton size="small" aria-label="edit" onClick={() => handleEditClick(row.id)}>
                        <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
            {filteredRows.length === 0 && (
              <Grid item xs={12}>
                <Card sx={{ p: 2, borderRadius: 2, textAlign: "center", width: '100%' }}>
                  <Typography color="text.secondary" fontSize={14}>No results found.</Typography>
                </Card>
              </Grid>
            )}
            {totalPages > 1 && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} flexWrap="wrap" gap={1}>
                  <Typography variant="body2" color="black">
                    Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)} to {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} entries
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, val) => setPage(val)}
                    color="primary"
                    size="small"
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box
              sx={{
                display: { xs: "none", sm: "grid" },
                gridTemplateColumns: { sm: gridColsDesktop },
                alignItems: "center",
                px: 1.2,
                py: 1,
                gap: 0.5,
                columnGap: 0.5,
              }}
            >
              {["Farmer Identity", "Mobile Number", "Village Name", "Status", "Actions"].map((h) => (
                <Typography key={h} variant="caption" fontWeight={600} sx={{ textTransform: "uppercase", textAlign: h === "Actions" ? "right" : "left" }}>
                  {h}
                </Typography>
              ))}
            </Box>

            {pagedRows.map((row) => (
              <Paper
                key={row.id}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  display: { xs: "none", sm: "grid" },
                  gridTemplateColumns: { sm: gridColsDesktop },
                  alignItems: "center",
                  border: "1px solid #E5E7EB",
                  px: 2,
                  py: 1.5,
                  gap: 0,
                  columnGap: 0,
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": { borderColor: "#2563EB", boxShadow: "0 4px 14px rgba(37,99,235,0.18)" },
                }}
              >
                <Box display="flex" alignItems="center" gap={1.25}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ lineHeight: 1.1 }}>
                      {row.name}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2">{row.mobile}</Typography>
                <Typography variant="body2">{villageMap[row.village_id] || row.village_id || "-"}</Typography>

                <Chip
                  label={row.status || "-"}
                  size="small"
                  sx={{ width: 90, height: 24, ml: -2, ...getStatusChipStyles(row.status), "& .MuiChip-label": { fontSize: 12, fontWeight: 600 } }}
                />

                <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                  <IconButton size="small" aria-label="edit" onClick={() => handleEditClick(row.id)}>
                    <EditOutlinedIcon fontSize="small" sx={{ color: "#2563EB", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.25))" }} />
                  </IconButton>
                </Box>
              </Paper>
            ))}
            {filteredRows.length === 0 && (
              <Card sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
                <Typography color="text.secondary">No results found.</Typography>
              </Card>
            )}
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
                <Typography variant="body2" >
                  Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredRows.length)} to {Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length} entries
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, val) => setPage(val)}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    {/* Edit Reference Dialog */}
    <Dialog open={editOpen} onClose={handleEditClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Reference</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Name"
            value={editValues.name}
            onChange={e => setEditValues(v => ({ ...v, name: e.target.value }))}
            fullWidth
            size="small"
            autoFocus
          />
          <TextField
            label="Mobile Number"
            value={editValues.mobile}
            onChange={e => setEditValues(v => ({ ...v, mobile: e.target.value }))}
            fullWidth
            size="small"
            inputProps={{ maxLength: 10 }}
          />
          <TextField
            label="Village"
            select
            value={editValues.village_id}
            onChange={e => setEditValues(v => ({ ...v, village_id: e.target.value }))}
            fullWidth
            size="small"
          >
            {villageList.map(v => (
              <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
            ))}
          </TextField>
          <TextField
  label="Status"
  select
  value={editValues.status}
  onChange={e => setEditValues(v => ({ ...v, status: e.target.value }))}
  fullWidth
  size="small"
>
  {['Converted', 'ReContact', 'Pending', 'Inactive'].map(s => (
    <MenuItem key={s} value={s}>{s}</MenuItem>
  ))}
</TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleEditClose}>Cancel</Button>
        <Button onClick={handleEditSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  </Box>
  );
}

export default FarmerReferenceLead;